import { Children, cloneElement, Fragment, isValidElement, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Button from './Button'

// Only collapse plain action groups; preserve mixed controls and interactive wrappers.
export function collectRowActions(children) {
    const result = []
    for (const child of Children.toArray(children)) {
        if (typeof child === 'string' && !child.trim()) continue
        if (!isValidElement(child)) return null
        if (child.type === Button || child.type === 'button' || child.type === 'a') result.push(child)
        else if (child.type === Fragment || (['div', 'span'].includes(child.type) && !Object.keys(child.props).some(key => key.startsWith('on')))) {
            const nested = collectRowActions(child.props.children)
            if (!nested) return null
            result.push(...nested)
        } else return null
    }
    return result.map((action, index) => cloneElement(action, { key: index }))
}

export default function ActionMenu({ children, className = '', label = 'More actions' }) {
    const actions = collectRowActions(children) || Children.toArray(children)
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState({ top: 0, left: 0 })
    const root = useRef(null)
    const popup = useRef(null)
    const trigger = useRef(null)
    useLayoutEffect(() => {
        if (!open) return
        const rect = trigger.current.getBoundingClientRect()
        const menu = popup.current.getBoundingClientRect()
        setPosition({
            left: Math.max(8, Math.min(rect.right - menu.width, window.innerWidth - menu.width - 8)),
            top: rect.bottom + menu.height + 8 > window.innerHeight ? Math.max(8, rect.top - menu.height - 6) : rect.bottom + 6,
        })
        popup.current.querySelector('button:not(:disabled)')?.focus()
    }, [open])
    useEffect(() => {
        if (!open) return
        const dismiss = event => {
            if (!root.current?.contains(event.target) && !popup.current?.contains(event.target)) setOpen(false)
        }
        const escape = event => { if (event.key === 'Escape') { setOpen(false); trigger.current?.focus() } }
        const reposition = event => { if (!popup.current?.contains(event.target)) setOpen(false) }
        document.addEventListener('pointerdown', dismiss)
        document.addEventListener('keydown', escape)
        window.addEventListener('resize', reposition)
        window.addEventListener('scroll', reposition, true)
        return () => {
            document.removeEventListener('pointerdown', dismiss)
            document.removeEventListener('keydown', escape)
            window.removeEventListener('resize', reposition)
            window.removeEventListener('scroll', reposition, true)
        }
    }, [open])
    return <div ref={root} className={'uiActionMenu ' + className}>
        {actions.length <= 1 ? actions : <>

            <Button ref={trigger} type="button" size="icon" title="More actions" aria-label="More actions" aria-expanded={open} onClick={event => { event.stopPropagation(); setOpen(!open) }}>&hellip;</Button>
            {open && createPortal(<div ref={popup} role="group" aria-label={label} className="uiActionMenuPopover" style={position}>
                {actions.map((action, index) => {
                    const { children: originalChildren, className: originalClass, style, size, ...props } = action.props
                    return <Button {...props} as={action.type === 'a' ? 'a' : props.as || 'button'} key={action.key || index}
                        type={props.type || 'button'} size='sm' className='uiActionMenuAction'
                        variant={props.variant || (/danger|delete/i.test(originalClass || '') ? 'danger' : 'secondary')}
                        onClick={event => {
                            event.stopPropagation()
                            if (props.disabled) { event.preventDefault(); return }
                            setOpen(false)
                            props.onClick?.(event)
                        }}>
                        {props['aria-label'] || props.title || originalChildren}
                    </Button>
                })}
            </div>, document.body)}
        </>}
    </div>
}
